import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Feather,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Layers,
  ShieldCheck,
  PhoneCall,
  ShoppingBag,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import AnimatedLogo from "../components/AnimatedLogo";
import { updateSEO } from "../utils/seo";
import "./About.css";

export default function About() {
  const { t, isRTL, language } = useLanguage();

  useEffect(() => {
    updateSEO({
      title: t("about.metaTitle"),
      description: isRTL
        ? "تعرف على قصة تيري لاند (Terry Land) الرائدة في صناعة برانص الحمام والفوط الفندقية ومفروشات العرايس منذ عام 1956، والجمع بين راحة القطن وتقنيات المايكروفايبر."
        : "Discover the heritage of Terry Land, crafting luxury bathrobes, towels, and bridal linens since 1956 with premium cotton and microfiber craftsmanship.",
      canonical: "https://terryland.net/about",
    });
  }, [t, language, isRTL]);

  const goals = [
    {
      num: "01",
      titleKey: "about.goal1Title",
      descKey: "about.goal1Desc",
    },
    {
      num: "02",
      titleKey: "about.goal2Title",
      descKey: "about.goal2Desc",
    },
    {
      num: "03",
      titleKey: "about.goal3Title",
      descKey: "about.goal3Desc",
    },
    {
      num: "04",
      titleKey: "about.goal4Title",
      descKey: "about.goal4Desc",
    },
    {
      num: "05",
      titleKey: "about.goal5Title",
      descKey: "about.goal5Desc",
    },
    {
      num: "06",
      titleKey: "about.goal6Title",
      descKey: "about.goal6Desc",
    },
  ];

  const stats = [
    {
      valueKey: "about.stat1Value",
      labelKey: "about.stat1Label",
    },
    {
      valueKey: "about.stat2Value",
      labelKey: "about.stat2Label",
    },
    {
      valueKey: "about.stat3Value",
      labelKey: "about.stat3Label",
    },
    {
      valueKey: "about.stat4Value",
      labelKey: "about.stat4Label",
    },
  ];

  const craftSpecs = [
    {
      icon: Layers,
      titleKey: "about.craft1Title",
      descKey: "about.craft1Desc",
      stat: "GSM",
    },
    {
      icon: Sparkles,
      titleKey: "about.craft2Title",
      descKey: "about.craft2Desc",
      stat: "100% Combed",
    },
    {
      icon: ShieldCheck,
      titleKey: "about.craft3Title",
      descKey: "about.craft3Desc",
      stat: "OEKO-TEX Class",
    },
  ];

  return (
    <article className="about-page" dir={isRTL ? "rtl" : "ltr"}>
      {/* ── Breadcrumb & Navigation Bar ── */}
      <nav className="about-breadcrumb" aria-label="Breadcrumb">
        <div className="container about-breadcrumb__inner">
          <Link to="/" className="about-breadcrumb__link">
            {t("about.breadcrumbHome")}
          </Link>
          <span className="about-breadcrumb__sep">/</span>
          <span className="about-breadcrumb__current" aria-current="page">
            {t("about.breadcrumbCurrent")}
          </span>
        </div>
      </nav>

      {/* ── Editorial Hero Section ── */}
      <header className="about-hero">
        <div className="container about-hero__container">
          <div className="about-hero__emblem-wrap">
            <AnimatedLogo size={240} />
          </div>

          <div className="about-hero__text-content">
            <div className="about-eyebrow-pill">
              <span>{t("about.heroEyebrow")}</span>
            </div>

            <h1 className="about-hero__title">
              {t("about.heroTitle")}
            </h1>

            <p className="about-hero__lead">
              {t("about.heroSubtitle")}
            </p>
          </div>
        </div>
      </header>

      {/* ── Heritage & Story Section ── */}
      <section className="about-section about-story-section">
        <div className="container about-heritage-container">
          <div className="about-heritage-content">
            <span className="about-subheading-tag">
              {t("about.storyBadge")}
            </span>
            <h2 className="about-heritage-title">
              {t("about.storyTitle")}
            </h2>
            <p className="about-heritage-lead">
              {t("about.storyP1")}
            </p>
            <p className="about-heritage-body">
              {t("about.storyP2")}
            </p>

            {/* Core Values: Innovation, Quality, Comfort */}
            <div className="about-heritage-values">
              <div className="about-heritage-value-card">
                <div className="about-heritage-value-icon-box">
                  <Sparkles size={18} />
                </div>
                <div>
                  <h4 className="about-heritage-value-title">{t("about.storyValue1")}</h4>
                  <p className="about-heritage-value-desc">{t("about.storyValue1Desc")}</p>
                </div>
              </div>

              <div className="about-heritage-value-card">
                <div className="about-heritage-value-icon-box">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h4 className="about-heritage-value-title">{t("about.storyValue2")}</h4>
                  <p className="about-heritage-value-desc">{t("about.storyValue2Desc")}</p>
                </div>
              </div>

              <div className="about-heritage-value-card">
                <div className="about-heritage-value-icon-box">
                  <Feather size={18} />
                </div>
                <div>
                  <h4 className="about-heritage-value-title">{t("about.storyValue3")}</h4>
                  <p className="about-heritage-value-desc">{t("about.storyValue3Desc")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="about-heritage-showcase">
            <div className="about-heritage-card">
              <div className="about-heritage-card__glow" />
              <div className="about-heritage-card__seal">
                <div className="about-heritage-card__year-badge">
                  <span className="about-heritage-card__est">EST.</span>
                  <span className="about-heritage-card__year">{t("about.storyCardYear")}</span>
                </div>
                <div className="about-heritage-card__seal-divider" />
                <span className="about-heritage-card__heritage-text">{t("about.storyCardTag")}</span>
              </div>
              <div className="about-heritage-card__content">
                <blockquote className="about-heritage-card__quote">
                  {t("about.storyCardQuote")}
                </blockquote>
                <div className="about-heritage-card__author-box">
                  <span className="about-heritage-card__author">{t("about.storyCardAuthor")}</span>
                  <span className="about-heritage-card__subtitle">{t("about.storyCardSubtitle")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Vision & Strategic Goals ── */}
      <section className="about-section about-vision-goals-section">
        <div className="container">
          {/* Vision Statement */}
          <div className="about-vision-block">
            <div className="about-vision-card">
              <div className="about-vision-card__header">
                <span className="about-subheading-tag">{t("about.visionEyebrow")}</span>
                <h2 className="about-vision-title">{t("about.visionTitle")}</h2>
              </div>
              <p className="about-vision-text">
                {t("about.visionText")}
              </p>
            </div>
          </div>

          {/* Strategic Goals Grid */}
          <div className="about-goals-wrapper">
            <div className="about-section-header about-goals-header">
              <span className="about-subheading-tag">{t("about.goalsEyebrow")}</span>
              <h2 className="about-section-heading">{t("about.goalsTitle")}</h2>
              <p className="about-section-subtitle">{t("about.goalsSubtitle")}</p>
            </div>

            <div className="about-goals-grid">
              {goals.map((goal, idx) => (
                <div key={idx} className="about-goal-card">
                  <div className="about-goal-card__header">
                    <span className="about-goal-card__num">{goal.num}</span>
                    <span className="about-goal-card__line" />
                  </div>
                  <h3 className="about-goal-card__title">{t(goal.titleKey)}</h3>
                  <p className="about-goal-card__desc">{t(goal.descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Key Milestones & Numbers ── */}
      <section className="about-section about-stats-section">
        <div className="container">
          <div className="about-section-header">
            <span className="about-subheading-tag">{t("about.statsEyebrow")}</span>
            <h2 className="about-section-heading">{t("about.statsTitle")}</h2>
          </div>

          <div className="about-stats-grid">
            {stats.map((stat, idx) => (
              <div key={idx} className="about-stat-card">
                <span className="about-stat-card__num">{t(stat.valueKey)}</span>
                <span className="about-stat-card__label">{t(stat.labelKey)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Anatomy of Quality (Craft Specs) ── */}
      <section className="about-section about-craft-section">
        <div className="container">
          <div className="about-section-header">
            <span className="about-subheading-tag">{t("about.craftEyebrow")}</span>
            <h2 className="about-section-heading">{t("about.craftTitle")}</h2>
            <p className="about-section-subtitle">{t("about.craftSubtitle")}</p>
          </div>

          <div className="about-craft-grid">
            {craftSpecs.map((spec, idx) => {
              const IconComp = spec.icon;
              return (
                <div key={idx} className="about-craft-card">
                  <div className="about-craft-card__badge">{spec.stat}</div>
                  <div className="about-craft-card__icon">
                    <IconComp size={26} />
                  </div>
                  <h3 className="about-craft-card__title">{t(spec.titleKey)}</h3>
                  <p className="about-craft-card__desc">{t(spec.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ── Next Step Conversion CTAs ── */}
      <section className="about-cta-section">
        <div className="container">
          <div className="about-cta-card">
            <div className="about-cta-content">
              <h2 className="about-cta-title">{t("about.ctaTitle")}</h2>
              <p className="about-cta-sub">{t("about.ctaSub")}</p>
              <div className="about-cta-buttons">
                <Link to="/shop" className="btn-primary about-cta-btn-primary">
                  <ShoppingBag size={17} />
                  <span>{t("about.ctaShop")}</span>
                  {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                </Link>
                <Link to="/contact" className="btn-outline about-cta-btn-secondary">
                  <PhoneCall size={16} />
                  <span>{t("about.ctaContact")}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
